import axios from "./axios";

const registerUser = async (userData: {
  email: string;
  password: string;
  role: string;
  company?: string;
  region?: string;
  city?: string;
}) => {
  try {
    const endpoint = "/user";

    const response = await axios.post(endpoint, userData);
    return response.data;
  } catch (error: unknown) {
    return error;
  }
};

const getMyUser = async () => {
  try {
    const endpoint = "/user/me";
    const response = await axios.get(endpoint);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching my user:", error);
    return {
      error:
        error?.response?.data?.message ||
        error?.message ||
        "Internal Server Error",
    };
  }
};

const updateMyUser = async (userData: {
  name?: string;
  email?: string;
  company?: string;
  region?: string;
  city?: string;
  secondaryCity?: string;
  notifyAnniversary?: boolean;
  notifyKeeperPending?: boolean;
}) => {
  try {
    const endpoint = "/user/me";

    const response = await axios.patch(endpoint, userData);
    return response.data;
  } catch (error: any) {
    console.error("Error updating my user:", error);
    return {
      error:
        error?.response?.data?.message ||
        error?.message ||
        "Internal Server Error",
    };
  }
};

const updateUserAndCompany = async (formData: FormData, id: string) => {
  try {
    const endpoint = `/user/${id}`;

    const response = await axios.patch(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Error updating user and company:", error);
    return {
      error:
        error?.response?.data?.message ||
        error?.message ||
        "Internal Server Error",
    };
  }
};

const deleteMyUser = async () => {
  try {
    const endpoint = "/user/me";

    const response = await axios.delete(endpoint);
    return response.data;
  } catch (error: any) {
    console.error("Error deleting my user:", error);
    return {
      error:
        error?.response?.data?.message ||
        error?.message ||
        "Internal Server Error",
    };
  }
};

const changeSlug = async (slug: string) => {
  const endpoint = "/user/me/slug-key";

  const response = await axios.patch(
    endpoint,
    { slugKey: slug },
    { withCredentials: true },
  );

  return response.data;
};

const getMyCards = async () => {
  try {
    const endpoint = "/user/me/cards";
    const response = await axios.get(endpoint);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching my cards:", error);
    return {
      error:
        error?.response?.data?.message ||
        error?.message ||
        "Internal Server Error",
    };
  }
};

const downloadCard = async (cardId: string) => {
  const endpoint = `/user/me/download/${cardId}`;

  const response = await axios.get(endpoint);

  return response.data;
};

const getMyKeeperStatus = async () => {
  try {
    const endpoint = "/user/me/keeper";
    const response = await axios.get(endpoint);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching my keeper status:", error);
    return {
      error:
        error?.response?.data?.message ||
        error?.message ||
        "Internal Server Error",
    };
  }
};

const updateKeeperStatus = async (id: string) => {
  const endpoint = `/user/me/keeper/${id}`;

  const response = await axios.patch(endpoint);

  return response.data;
};

const saveContact = async (payload: any) => {
  const endpoint = `/post/contact`;

  const response = await axios.post(endpoint, payload);

  return response.data;
};

const saveObitNotification = async (payload: any) => {
  const endpoint = `/post/obit-notification`;

  const response = await axios.post(endpoint, payload);

  return response.data;
};

const getMyKeeperNotifications = async () => {
  try {
    const endpoint = "/user/me/keeper-gifts";
    const response = await axios.get(endpoint);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching my keeper notifications:", error);
    return {
      error:
        error?.response?.data?.message ||
        error?.message ||
        "Internal Server Error",
    };
  }
};

const updateCardStatus = async (id: string) => {
  const endpoint = `/user/me/notify/${id}`;

  const response = await axios.post(endpoint);

  return response.data;
};

const getSponsors = async (params?: any) => {
  try {
    let endpoint = "/user/me/sponsors";

    if (typeof params === "string") {
      endpoint += params;
      const response = await axios.get(endpoint);
      return response.data;
    } else {
      const response = await axios.get(endpoint, { params });
      return response.data;
    }
  } catch (error: any) {
    console.error("Error fetching sponsors:", error);
    return {
      error:
        error?.response?.data?.message ||
        error?.message ||
        "Internal Server Error",
    };
  }
};

// Registration with the e-mail and the password the visitor chose; the server also e-mails a welcome
// message that repeats the password.
const register = async (data: {
  email: string;
  password: string;
  acceptTerms: boolean;
  receiveEmails: boolean;
}): Promise<{ message?: string; error?: string }> => {
  try {
    const response = await axios.post("/auth/register", data);
    return response.data;
  } catch (error: any) {
    return {
      error:
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Registracija ni uspela. Poskusi znova.",
    };
  }
};

// "Pozabljeno geslo" / "Spremeni geslo": the server e-mails a link to the page where the owner of the
// address chooses a new password.
const requestNewPassword = async (
  email: string
): Promise<{ message?: string; error?: string }> => {
  try {
    const response = await axios.post("/auth/forgot-password", { email });
    return response.data;
  } catch (error: any) {
    return {
      error:
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Povezave trenutno ni mogoče poslati. Poskusi znova.",
    };
  }
};

// The page behind that link: sets the new password; answers with the e-mail so the page can log in.
const resetPassword = async (
  token: string,
  password: string
): Promise<{ email?: string; message?: string; error?: string }> => {
  try {
    const response = await axios.post("/auth/reset-password", { token, password });
    return response.data;
  } catch (error: any) {
    return {
      error:
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Gesla ni bilo mogoče spremeniti. Poskusi znova.",
    };
  }
};

const userService = {
  register,
  resetPassword,
  requestNewPassword,
  registerUser,
  getMyUser,
  updateMyUser,
  deleteMyUser,
  changeSlug,
  updateUserAndCompany,
  getMyCards,
  downloadCard,
  getMyKeeperStatus,
  updateKeeperStatus,
  saveContact,
  saveObitNotification,
  getMyKeeperNotifications,
  updateCardStatus,
  getSponsors,
};

export default userService;
